import { injectable, inject } from "inversify";
import { BoxOptions } from "../menu/message-box";
import TYPES from "../../ioc/types";
import { ThemeManager } from "../design-system/theme-manager";

export interface GeometryService {
    initGraphicContext(graphics: Phaser.GameObjects.Graphics);

    drawBox(x: number, y: number, width: number, height: number, boxOptions?: BoxOptions): void;
}

@injectable()
export class DefaultGeometryService implements GeometryService {
    private graphics: Phaser.GameObjects.Graphics;
    private DEFAULT_OPTIONS: BoxOptions;

    constructor(@inject(TYPES.ThemeManager) private themeManager: ThemeManager) {
        // Initialize default options using theme - RPG aesthetic
        this.DEFAULT_OPTIONS = {
            lineWidth: this.themeManager.getBorderWidth("thick"),
            lineColor: this.themeManager.getColor("ui.border"),
            fillColor: this.themeManager.getColor("ui.backgroundLight"),
            fillAlpha: 0.85,
            frameAlpha: 1,
        } as BoxOptions;
    }

    public initGraphicContext(graphics: Phaser.GameObjects.Graphics) {
        this.graphics = graphics;
    }

    public drawBox(
        x: number,
        y: number,
        width: number,
        height: number,
        boxOptions?: BoxOptions
    ): void {
        const lineWidth =
            boxOptions && boxOptions.lineWidth !== undefined
                ? boxOptions.lineWidth
                : this.DEFAULT_OPTIONS.lineWidth;
        const lineColor =
            boxOptions && boxOptions.lineColor !== undefined
                ? boxOptions.lineColor
                : this.DEFAULT_OPTIONS.lineColor;
        const fillColor =
            boxOptions && boxOptions.fillColor !== undefined
                ? boxOptions.fillColor
                : this.DEFAULT_OPTIONS.fillColor;
        const fillAlpha =
            boxOptions && boxOptions.fillAlpha !== undefined
                ? boxOptions.fillAlpha
                : this.DEFAULT_OPTIONS.fillAlpha;
        const frameAlpha =
            boxOptions && boxOptions.frameAlpha !== undefined
                ? boxOptions.frameAlpha
                : this.DEFAULT_OPTIONS.frameAlpha;

        const radius = this.themeManager.getBorderRadius("lg");
        const glowSize = this.themeManager.getGlowSize("md");
        const glowColor = this.themeManager.getColor("ui.borderGlow");

        // Draw outer glow
        this.graphics.lineStyle(glowSize, glowColor, 0.3);
        this.graphics.strokeRoundedRect(
            x - glowSize / 2,
            y - glowSize / 2,
            width + glowSize,
            height + glowSize,
            radius + glowSize / 2
        );

        // Draw main background
        this.graphics.fillStyle(fillColor, fillAlpha);
        this.graphics.fillRoundedRect(x, y, width, height, radius);

        // Draw inner highlight for depth
        const highlightColor = this.themeManager.getColor("primaryLight");
        this.graphics.fillStyle(highlightColor, 0.15);
        this.graphics.fillRoundedRect(x, y, width, height / 4, {
            tl: radius,
            tr: radius,
            bl: 0,
            br: 0,
        });

        // Draw border
        this.graphics.lineStyle(lineWidth, lineColor, frameAlpha);
        this.graphics.strokeRoundedRect(x, y, width, height, radius);
    }
}
